import { StoryShot } from '../lib/StoryShot';
import { VideoLength, StoryLanguage, StoryStructure } from '../types';
import { logger, logAPICall } from '../utils/logger';
import { serverVideoService } from './serverVideoService';

// Use the modularized StoryShot class
// Vite requires environment variables to start with VITE_
const geminiApiKey = import.meta.env.VITE_GEMINI_API_KEY || '';

if (!geminiApiKey) {
    logger.error("ENV_VAR_MISSING", { variable: "VITE_GEMINI_API_KEY", message: "Gemini API key not configured" });
}

// Video generation is handled server-side through /api/video/generate
// No need for VITE_GOOGLE_CLOUD_API_KEY on the client
const storyShot = new StoryShot({ 
    apiKey: geminiApiKey
});

export const generateStoryScript = async (
  topic: string, 
  length: VideoLength | number, 
  language: StoryLanguage = StoryLanguage.ENGLISH,
  structure: StoryStructure = StoryStructure.HOOK_TO_END
): Promise<string> => {
  if (!geminiApiKey || geminiApiKey === 'your_gemini_api_key_here') {
    logger.error("API_KEY_INVALID", { service: "Gemini", configured: !!geminiApiKey });
    return "Error: Gemini API key is not configured. Please set your VITE_GEMINI_API_KEY in the environment variables.";
  }
  
  const startTime = Date.now();
  try {
    const result = await storyShot.generateScript(topic, length, language, structure);
    logAPICall("Gemini", "generateScript", true, Date.now() - startTime);
    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error("SCRIPT_GENERATION_ERROR", { error: errorMessage, topic, length });
    logAPICall("Gemini", "generateScript", false, Date.now() - startTime, errorMessage);
    if (error instanceof Error) {
      return `Error: Failed to generate script. ${error.message}`;
    }
    return "Error: An unknown error occurred while generating the script.";
  }
};


export const generateVideoFromScript = async (script: string, segment?: number, model: string = 'auto', provider: string = 'auto'): Promise<string> => {
  logger.info("VIDEO_GENERATION_REQUEST", { scriptLength: script?.length, segment, model, provider });
  
  const startTime = Date.now();
  
  // Use server-side video generation for security
  // This prevents exposing API keys to the client
  try {
    logger.info("VIDEO_GENERATION_USING_SERVER", { service: "ServerVideoService", model, provider });
    
    const result = await serverVideoService.generateVideo(script, segment, model, provider);
    
    const videoType = result?.startsWith('http') ? 'url' : 
                     result?.startsWith('data:') ? 'data-url' : 'unknown';
    
    logger.info("VIDEO_GENERATION_SUCCESS", { 
      videoType, 
      resultLength: result?.length,
      duration: Date.now() - startTime 
    });
    
    logAPICall("ServerVideo", "generateVideo", true, Date.now() - startTime);
    return result;
    
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const errorReason = (error as any)?.reason;
    const providerStatus = (error as any)?.providerStatus;
    
    logger.error("VIDEO_GENERATION_ERROR", { 
      error: errorMessage,
      reason: errorReason,
      providerStatus: providerStatus,
      duration: Date.now() - startTime
    });
    
    logAPICall("ServerVideo", "generateVideo", false, Date.now() - startTime, errorMessage);
    
    // Check if we should offer fallback
    const shouldOfferFallback = errorReason === 'MISSING_API_KEY' || 
                                errorReason === 'INVALID_API_KEY' ||
                                errorReason === 'MODEL_NOT_FOUND' ||
                                providerStatus === 403 ||
                                providerStatus === 404;
    
    if (shouldOfferFallback) {
      logger.info("VIDEO_GENERATION_OFFERING_FALLBACK", { reason: errorReason });
      
      // Throw error with fallback suggestion
      const fallbackMessage = `\n\nNote: Storyboard preview is available as an alternative.`;
      throw new Error(errorMessage + fallbackMessage);
    } else {
      // For other errors (rate limits, server errors), just throw the error
      throw error;
    }
  }
};